import classNames from "classnames";
import DashboardContainer from "components/dashboard/DashboardContainer";
import { Header } from "components/DesignSystem/Header/Header";
import { Search } from "components/DesignSystem/Search/Search";
import { colors } from "constants/colors";
import { useCopyToClipboard } from "hooks/useCopyToClipboard";
import { useQuery, useUpdateQuery } from "hooks/useQuery";
import { ChangeEvent, useMemo } from "react";
import { debounce } from "utils/debouncing";

function flattenColors(obj: Record<string, any>): Record<string, string>[] {
  const result: Record<string, string>[] = [];

  const walk = (current: any, path: string[]) => {
    for (const key in current) {
      const value = current[key];
      const newPath = [...path, key];
      if (typeof value === "string") {
        result.push({
          name: `${newPath.join("-")}`,
          hex: value,
          group: path[0] || "base",
          shade: key,
        });
      } else {
        walk(value, newPath);
      }
    }
  };

  walk(obj, []);
  return result;
}

function groupBy<T>(items: T[], key: keyof T) {
  return items.reduce((acc, item) => {
    const group = item[key] as string;
    acc[group] = acc[group] || [];
    acc[group].push(item);
    return acc;
  }, {} as Record<string, T[]>);
}

export default function ColorGrid() {
  const { copyToClipboard } = useCopyToClipboard();
  const flatColors = flattenColors(colors);
  const grouped = groupBy(flatColors, "group");
  const query = useQuery();
  const search = query.get("search");
  const { update: updateQuery } = useUpdateQuery();

  const sortShades = (a: any, b: any) => {
    const na = parseInt(a.shade);
    const nb = parseInt(b.shade);
    return isNaN(na) || isNaN(nb) ? a.shade.localeCompare(b.shade) : na - nb;
  };

  const handleSearchChange = debounce((e: ChangeEvent<HTMLInputElement>) => {
    updateQuery({ query: "search", value: e.target.value || null });
  });

  const filteredGroupedColors = useMemo(() => {
    if (!search) return grouped;

    const lowerSearch = search.toLowerCase();
    return Object.fromEntries(
      Object.entries(grouped)
        .map(([group, items]) => [
          group,
          items.filter(
            (color) =>
              color.name.toLowerCase().includes(lowerSearch) ||
              color.hex.toLowerCase().includes(lowerSearch)
          ),
        ])
        .filter(([, items]) => items.length > 0)
    );
  }, [grouped, search]);

  return (
    <DashboardContainer className="t-px-10 t-pb-4">
      <DashboardContainer.Header sticky>
        <Header
          className="t-py-3"
          title="Color Palette"
          v2
          bottom={
            <p className="t-text-body-sm t-text-neutral-500 t-m-0">
              Click on an individual color to copy classname.
            </p>
          }
          right={
            <Search
              autoFocus
              onChange={handleSearchChange}
              defaultValue={search || ""}
              placeholder="Search..."
            />
          }
        />
      </DashboardContainer.Header>

      <DashboardContainer.Content className="t-space-y-8 t-my-4">
        {Object.entries(filteredGroupedColors).map(([group, items]) => (
          <div key={group}>
            <div className="t-mb-2 t-font-semibold t-capitalize">{group}</div>
            <div className="t-flex t-gap-2 t-items-center t-flex-wrap">
              {(items as typeof flatColors)
                .sort(sortShades)
                .map(({ name, hex }) => (
                  <button
                    key={name}
                    className="all:unset t-cursor-pointer t-flex t-flex-col t-items-center t-gap-1 t-text-[10px] t-w-max t-mx-2"
                    onClick={() => copyToClipboard(name)}
                    title={`${name} → ${hex}`}
                  >
                    <div className="t-text-center t-break-words t-text-xs t-text-wrap t-max-w-full">
                      {hex}
                    </div>
                    <div
                      className={classNames(
                        "t-rounded t-w-8 t-h-8 t-border t-border-collapse t-border-solid t-border-neutral-10"
                      )}
                      style={{ backgroundColor: hex }}
                    />
                    <div className="t-text-center t-break-words t-text-xs t-text-wrap t-max-w-full">
                      {name}
                    </div>
                  </button>
                ))}
            </div>
          </div>
        ))}
      </DashboardContainer.Content>
    </DashboardContainer>
  );
}
